import { Navigate, Outlet, useLocation } from "react-router";
import type { ReactNode } from "react";
import { useAuth } from "@/domains/shared/hooks/useAuth";
import { AccessLevel, type PermissionRequirement } from "@/app/routes/types/routeAccess";
import { requiresCouncil } from "@/domains/shared/constants/councilRequirements";
import {
    isClinicalAccessBlocked,
    isNavPathAllowedWhenClinicalAccessBlocked,
    resolveClinicalAccessRedirectPath,
    requiresPaymentBeforeAccess,
    THERAPIST_PROFILE_PATH,
} from "@/domains/subscription/accountAccess";
import {
    hasSecretaryPermission,
    secretaryHasFinanceMenu,
} from "@/domains/shared/services/secretaryPermissions";
import {
    clinicSecretaryHasFinanceMenu,
    clinicSecretaryHasPermission,
} from "@/domains/multi/clinicSecretaryPermissions";
import {
    ADMIN_THERAPISTS_PATH,
    needsEmailVerification,
    isEmailVerificationAllowedPath,
} from "@/domains/shared/services/emailVerification";
import { Roles } from "@/domains/shared/models/roles";

function roleHome(role: string): string {
    if (role === Roles.Therapist) {
        return "/profissional";
    }
    if (role === Roles.Secretary) {
        return "/administrativo";
    }
    if (role === Roles.Admin) {
        return ADMIN_THERAPISTS_PATH;
    }
    return "/familia";
}

type AccessGuardProps = {
    requirements: PermissionRequirement[];
    children?: ReactNode;
};

/**
 * Config-driven route guard. Renders `children` when provided, otherwise `<Outlet />`.
 */
export function AccessGuard({ requirements, children }: AccessGuardProps) {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="h-10 w-10 border-2 border-border border-t-primary rounded-full animate-spin" />
            </div>
        );
    }

    for (const req of requirements) {
        switch (req.level) {
            case AccessLevel.AUTHENTICATED: {
                if (!user) {
                    return <Navigate to="/login" state={{ from: location }} replace />;
                }
                if (req.roles && req.roles.length > 0 && !req.roles.includes(user.role)) {
                    return <Navigate to={roleHome(user.role)} replace />;
                }
                break;
            }
            case AccessLevel.EMAIL_VERIFIED: {
                if (user && needsEmailVerification(user) && !isEmailVerificationAllowedPath(location.pathname)) {
                    return <Navigate to="/verificar-email" replace state={{ from: location }} />;
                }
                break;
            }
            case AccessLevel.BILLING_ACTIVE: {
                if (user && isClinicalAccessBlocked(user)) {
                    const normalized = location.pathname.replace(/\/+$/, "");
                    if (!isNavPathAllowedWhenClinicalAccessBlocked(location.pathname, user)) {
                        if (normalized === "/profissional") {
                            return <Navigate to={THERAPIST_PROFILE_PATH} replace />;
                        }
                        return <Navigate to={resolveClinicalAccessRedirectPath(user)} replace />;
                    }
                }
                break;
            }
            case AccessLevel.COUNCIL_VERIFIED: {
                if (
                    user
                    && user.role === Roles.Therapist
                    && !requiresPaymentBeforeAccess(user)
                    && requiresCouncil(user.specialty)
                ) {
                    const hasCouncil = !!(user.councilNumber && String(user.councilNumber).trim());
                    const isCouncilPath = location.pathname.startsWith("/profissional/perfil");
                    if (!hasCouncil && !isCouncilPath) {
                        return <Navigate to="/profissional/perfil" replace />;
                    }
                }
                break;
            }
            case AccessLevel.SECRETARY_PERMISSION: {
                if (user?.role === Roles.Secretary) {
                    if (user.secretaryAccessBlocked) {
                        return <Navigate to="/administrativo" replace />;
                    }
                    const allowed = user.isClinicSecretary
                        ? req.requireFinanceMenu
                            ? clinicSecretaryHasFinanceMenu()
                            : req.permissions
                                ? clinicSecretaryHasPermission(...req.permissions)
                                : true
                        : req.requireFinanceMenu
                            ? secretaryHasFinanceMenu(user.secretaryPermissions)
                            : req.permissions
                                ? hasSecretaryPermission(user.secretaryPermissions, ...req.permissions)
                                : true;
                    if (!allowed) {
                        return <Navigate to="/administrativo" replace />;
                    }
                }
                break;
            }
            case AccessLevel.ADMIN: {
                if (user?.role !== Roles.Admin) {
                    return <Navigate to={user ? roleHome(user.role) : "/login"} replace />;
                }
                break;
            }
            case AccessLevel.CLINIC_MEMBER:
                break;
            default:
                break;
        }
    }

    return <>{children ?? <Outlet />}</>;
}
